import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { listTeamMembers } from "@/lib/api";
import VersionPill from "@/components/custom/VersionPill";


export default function MobileSelect() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const existing = localStorage.getItem("team_member_id");
    if (existing) { navigate("/mobile/today"); return; }
    listTeamMembers().then(data => setMembers(data.filter(m => m.member_type !== "sub_contractor"))).catch(() => {});
    // SW registration deliberately removed here — App.js already registers /sw.js once on mount
    // with `updateViaCache: 'none'`, which covers this page. Duplicate registration was harmless
    // but redundant.
  }, [navigate]);

  const handleLogin = async () => {
    if (!selected) return;
    setChecking(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-mobile`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ initials: selected.initials, password })
      });
      if (!resp.ok) throw new Error();
      localStorage.setItem("team_member_id", selected.id);
      localStorage.setItem("team_member_name", selected.name);
      localStorage.setItem("team_member_color", selected.color);
      localStorage.setItem("team_member_initials", selected.initials);
      navigate("/mobile/today");
    } catch {
      setError("Incorrect password");
      toast.error("Incorrect password");
    } finally { setChecking(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: "linear-gradient(135deg, #F5F3EE 0%, #EDE9E3 100%)" }}>
      <img src="/hps-login-logo.png" alt="Sion Group at HPS" className="h-40 object-contain mb-2" style={{ mixBlendMode: "multiply" }} />
      <p style={{ fontSize: "14px", color: "var(--text-muted)", marginBottom: "24px" }}>Powered by SION</p>

      {!selected ? (
        <div className="w-full max-w-sm" style={{ background: "#fff", borderRadius: "16px", padding: "32px 24px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
          <p className="text-sm mb-5 text-center" style={{ color: "var(--text-secondary)" }}>Select your name to continue</p>
          <div className="space-y-2">
            {members.map(m => (
              <button
                key={m.id}
                onClick={() => { setSelected(m); setPassword(""); setError(""); }}
                className="w-full flex items-center gap-3 p-4 rounded-xl transition-all active:scale-[0.98] hover:shadow-md"
                style={{ border: "1px solid var(--border)", background: "#FAFAF9" }}
                data-testid={`select-member-${m.initials}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: m.color || "#64748b" }}
                >
                  {m.initials}
                </div>
                <span className="font-medium text-base" style={{ color: "var(--text-primary)" }}>{m.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="w-full max-w-sm" style={{ background: "#fff", borderRadius: "16px", padding: "32px 24px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ backgroundColor: selected.color || "#64748b" }}
            >
              {selected.initials}
            </div>
            <div>
              <p className="font-semibold" style={{ color: "var(--text-primary)" }}>{selected.name}</p>
              <button onClick={() => { setSelected(null); setPassword(""); setError(""); }} style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                Not you?
              </button>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} autoComplete="on">
            <input type="text" name="username" autoComplete="username" value={selected.initials} readOnly className="hidden" />
            <div className="relative mb-4">
              <Input
                type={showPw ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                placeholder="Enter password"
                className="h-12 rounded-xl pr-10 text-base"
                style={{ border: error ? "2px solid var(--danger)" : "1px solid var(--border)" }}
                autoFocus
                data-testid="mobile-login-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}>
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {error && <p className="text-xs mb-3" style={{ color: "var(--danger)" }}>{error}</p>}
            <Button type="submit" className="w-full h-12 rounded-xl text-base font-semibold" style={{ background: "#E05C2A", color: "#fff" }}>
              Sign In
            </Button>
          </form>
        </div>
      )}
      <div style={{ marginTop: 24, paddingBottom: 12, opacity: 0.7 }}>
        <VersionPill />
      </div>
    </div>
  );
}
